import React, { useCallback, useEffect, useState } from 'react';
import LeaderboardPanel from '../ui/LeaderboardPanel';
import { API_ENDPOINTS } from '../../../utils/config';

const OUTCOME = {
    victory: 'victory', party_down: 'party down', time_up: 'time up',
    no_lives: 'out of lives', invaded: 'invaded', step_limit: 'survived',
};

/** "alice + Guest + carol" — guests stay anonymous. */
export function coopTeamName(entry) {
    const names = (entry?.players || []).map((p) => (p?.guest ? 'Guest' : p?.name || 'Guest'));
    return names.length ? names.join(' + ') : 'Team';
}

export function coopEntryDetail(entry) {
    const parts = [];
    if (entry?.outcome) parts.push(OUTCOME[entry.outcome] || entry.outcome);
    if (entry?.player_count) parts.push(`${entry.player_count}P`);
    if (Number.isFinite(entry?.duration_s)) {
        const s = Math.round(entry.duration_s);
        parts.push(`${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);
    }
    return parts.join(' · ');
}

/**
 * Top co-op team scores for one game (GET /api/coop/leaderboard), recorded
 * by the server when a co-op room finishes. Refetches when refreshKey changes.
 */
export default function CoopLeaderboard({ gameId, accentColor, refreshKey = 0, limit = 10 }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (typeof fetch !== 'function') { setLoading(false); return; }
        setLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.COOP_LEADERBOARD(gameId, limit));
            const data = res.ok ? await res.json() : {};
            setEntries(Array.isArray(data?.leaderboard) ? data.leaderboard : []);
        } catch (_) {
            setEntries([]);
        } finally {
            setLoading(false);
        }
    }, [gameId, limit]);

    useEffect(() => { load(); }, [load, refreshKey]);

    return (
        <div data-testid={`coop-leaderboard-${gameId}`}>
            <LeaderboardPanel
                entries={entries}
                loading={loading}
                onRefresh={load}
                accentColor={accentColor}
                formatScore={(e) => String(e.team_score ?? 0)}
                formatName={coopTeamName}
                formatTime={coopEntryDetail}
                emptyHint="No co-op team scores yet — finish a game together to get on the board."
                limit={limit}
                title="Co-op team leaderboard"
            />
        </div>
    );
}
